import React from 'react';
import { TRANSMISSION_CONFIG, PRUDENCE_NOTICE } from '../config/transmissionConfig';

const S = '#00F5A0';
const G = '#8892A4';

const MODE_BADGE_LABEL = {
  info:    'Information',
  direct:  'Accès direct',
  keys:    'Récupération',
  limited: 'Gestion limitée',
};

const AttributionSelector = ({
  availableCategories,
  selectedCategories,
  actExplicit,
  onCategoriesChange,
  onActExplicitChange,
  loadingState,
  disabled,
}) => {
  const isAttrRequired = selectedCategories.length > 0;
  const actRequired    = isAttrRequired && !actExplicit;

  const toggle = (cat) => {
    const next = selectedCategories.includes(cat)
      ? selectedCategories.filter(c => c !== cat)
      : [...selectedCategories, cat];
    onCategoriesChange(next);
  };

  return (
    <div>
      <p style={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.75)', margin: '0 0 14px', lineHeight: 1.5 }}>
        Quels éléments de transmission cette personne recevra-t-elle ?
      </p>

      {loadingState === 'loading' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: G, fontSize: 13, padding: '12px 0' }}>
          <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.1)', borderTopColor: S, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Chargement des catégories du coffre...
        </div>
      )}

      {loadingState === 'error' && (
        <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#fbbf24', lineHeight: 1.6 }}>
          Impossible de charger les catégories du coffre. Vous pouvez créer le bénéficiaire et configurer l'attribution plus tard.
        </div>
      )}

      {loadingState === 'ready' && availableCategories.length === 0 && (
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '14px 16px', fontSize: 13, color: G, lineHeight: 1.6 }}>
          Vous n'avez pas encore d'éléments dans votre coffre. Ajoutez des éléments avant de configurer la transmission.
        </div>
      )}

      {loadingState === 'ready' && availableCategories.length > 0 && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {availableCategories.map(cat => {
              const cfg = TRANSMISSION_CONFIG[cat];
              if (!cfg) return null;
              const checked = selectedCategories.includes(cat);
              return (
                <label
                  key={cat}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    background: checked ? 'rgba(0,245,160,0.05)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${checked ? 'rgba(0,245,160,0.2)' : 'rgba(255,255,255,0.07)'}`,
                    borderRadius: 10,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s ease',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(cat)}
                    disabled={disabled}
                    style={{ accentColor: S, width: 16, height: 16, flexShrink: 0, cursor: disabled ? 'not-allowed' : 'pointer' }}
                  />
                  <span style={{ flex: 1, fontSize: 14, color: checked ? '#fff' : 'rgba(255,255,255,0.7)', fontWeight: checked ? 600 : 400 }}>
                    {cfg.label}
                  </span>
                  <span style={{ background: 'rgba(0,245,160,0.1)', color: S, padding: '4px 8px', borderRadius: 4, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>
                    {MODE_BADGE_LABEL[cfg.mode] || cfg.mode}
                  </span>
                  <span style={{ background: 'rgba(255,255,255,0.05)', color: G, padding: '4px 8px', borderRadius: 4, fontSize: 11, flexShrink: 0 }}>
                    {cfg.delay}
                  </span>
                </label>
              );
            })}
          </div>

          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px 16px', marginBottom: 14, fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 }}>
            {PRUDENCE_NOTICE}
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: '14px 16px',
              background: isAttrRequired ? 'rgba(0,245,160,0.04)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${isAttrRequired ? 'rgba(0,245,160,0.15)' : 'rgba(255,255,255,0.06)'}`,
              borderRadius: 10,
              cursor: disabled ? 'not-allowed' : 'pointer',
              userSelect: 'none',
              transition: 'all 0.15s ease',
            }}
          >
            <input
              type="checkbox"
              checked={actExplicit}
              onChange={e => onActExplicitChange(e.target.checked)}
              disabled={disabled}
              style={{ accentColor: S, width: 16, height: 16, marginTop: 2, flexShrink: 0, cursor: disabled ? 'not-allowed' : 'pointer' }}
            />
            <div>
              <span style={{ fontSize: 13, color: isAttrRequired ? 'rgba(255,255,255,0.85)' : G, lineHeight: 1.6, display: 'block' }}>
                Je désigne explicitement cette personne pour recevoir les éléments sélectionnés ci-dessus, en cas de déclenchement du processus de transmission prévu par le service.
              </span>
              {actRequired && (
                <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: 'rgba(239,68,68,0.85)' }}>
                  Cochez pour valider la désignation
                </span>
              )}
            </div>
          </label>
        </>
      )}
    </div>
  );
};

export default AttributionSelector;
